import React, { createContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { UserRoleEnum } from "@/__generated__/types";
import { useTeammates } from "@/hooks/useTeammates";
import {
  ROLE_MAP,
  useTeammateTableConfig,
} from "./hooks/useTeammateTableConfig";
import { DataTable } from "@/components/DataTable";
import { Button } from "@/components/ui";
import { PlusCircle, UserSearch } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useRoute } from "@/routes/getRoute";
import { EmptyState } from "@/components/DataTable/States/Empty";
import { i18n } from "@/i18n";
import { SeatAlert } from "./Components/SeatAlert";
import { PermissionAlert } from "./Components/PermissionAlert";
import { CURRENT_USER_PERMISSIONS } from "@/api/queries/currentUserPermissions";
import { useQuery } from "@apollo/client";
import { useOrgDetails } from "@/hooks/useOrgDetails";

export const TeamTableContext = createContext<{
  refetch?: () => void;
}>({});

const DEFAULT_ROWS = 100;

export const TeamTable = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "teamManagement.table",
  });
  const { org, loading: orgLoading } = useOrgDetails();
  const [pageIndex, setPageIndex] = useState(0);
  const [after, setAfter] = useState<string | null>(null);
  const [before, setBefore] = useState<string | null>(null);
  const [filterRoles, setFilterRoles] = useState<UserRoleEnum[]>([]);
  const { columns, ordering } = useTeammateTableConfig();
  const navigate = useNavigate();
  const getRoute = useRoute();

  const handleFilterChange = (filterId: string, values: string[]): void => {
    setPageIndex(0);
    setAfter(null);
    setBefore(null);
    setFilterRoles(values.map((value) => value as UserRoleEnum));
  };

  const hasSeatsAvailable = Boolean(
    org?.seatsAvailable && org.seatsAvailable > 0,
  );

  const { data: permissionData } = useQuery(CURRENT_USER_PERMISSIONS, {
    variables: { organizationId: org?.id ?? "" },
    skip: !org?.id,
  });

  const { me } = permissionData ?? {};

  const { data, loading, refetch } = useTeammates({
    orgId: org?.id,
    roles: filterRoles,
    first: after || (!after && !before) ? DEFAULT_ROWS : undefined,
    last: before ? DEFAULT_ROWS : undefined,
    after,
    before,
    ordering: ordering,
  });

  const {
    users,
    totalCount,
    hasNextPage,
    hasPreviousPage,
    endCursor,
    startCursor,
  } = data;

  const renderInviteButton = (
    variant: "default" | "outline" = "outline",
    className = "h-8",
    showIcon = true,
  ) => {
    const handleClick = () => {
      if (me?.isSuperCounselor && hasSeatsAvailable) {
        navigate(getRoute("sendInvite"));
      }
    };

    const buttonText =
      variant === "default" ? t("teammates.emptyButton") : t("inviteTeammate");
    const ariaLabel =
      variant === "default"
        ? `${t("teammates.emptyButton")} - ${t("teammates.empty")}`
        : undefined;

    const button = (
      <Button
        className={className}
        type="button"
        variant={variant}
        onClick={handleClick}
        aria-label={ariaLabel}
      >
        {showIcon && <PlusCircle className="h-4 w-4" aria-label={buttonText} />}
        {buttonText}
      </Button>
    );

    if (me?.isSuperCounselor && !hasSeatsAvailable) {
      return <SeatAlert showAlert={true}>{button}</SeatAlert>;
    }

    return (
      <PermissionAlert showAlert={!me?.isSuperCounselor}>
        {button}
      </PermissionAlert>
    );
  };

  return (
    <TeamTableContext.Provider value={{ refetch }}>
      <DataTable
        data={users}
        columns={columns}
        isLoading={loading || orgLoading}
        pagination={
          <>
            <DataTable.PaginationLabel
              pageCount={getPageCount(totalCount)}
              pageIndex={pageIndex}
            />
            <DataTable.PaginationControls
              onNextPage={
                hasNextPage &&
                (() => {
                  setBefore(null);
                  setAfter(endCursor || null);
                  setPageIndex(pageIndex + 1);
                })
              }
              onPreviousPage={
                hasPreviousPage &&
                (() => {
                  setAfter(null);
                  setBefore(startCursor || null);
                  setPageIndex(pageIndex - 1);
                })
              }
            />
          </>
        }
        tableHeader={<DataTable.THeader />}
        tableBody={<DataTable.TBody />}
        emptyState={
          <EmptyState
            copy={t("teammates.empty")}
            button={renderInviteButton(
              "default",
              "mt-8 h-10 bg-green-700 hover:bg-green-800",
              false,
            )}
            icon={
              <UserSearch
                className="size-9 text-primary-light"
                aria-label={t("inviteTeammate")}
              />
            }
          />
        }
        topHeader={
          <>
            <DataTable.Title
              title={t("title")}
              description={t("description", {
                seats: org?.seatsAvailable ?? 0,
              })}
            />
            <div className="flex items-center gap-2">
              <DataTable.Filter
                customFilters={[
                  {
                    id: "role",
                    label: t("role"),
                    values: Object.values(UserRoleEnum).map((type) => ({
                      value: type,
                      renderComponent: ({ value }) =>
                        i18n.t(ROLE_MAP[value as UserRoleEnum]),
                    })),
                    avatarFilterLabel: false,
                  },
                ]}
                onFilterChange={handleFilterChange}
                displayValue={filterRoles
                  .map((value) => i18n.t(ROLE_MAP[value as UserRoleEnum]))
                  .join(", ")}
              />
              {renderInviteButton()}
            </div>
          </>
        }
      />
    </TeamTableContext.Provider>
  );
};

function getPageCount(totalCount: number) {
  return Math.ceil(totalCount / DEFAULT_ROWS);
}
